/**
 * @fileoverview Controller Hook para la pantalla principal del Inbox.
 * Orquesta Data Layer, UI Layer, Business Layer y Socket Layer.
 *
 * @module useInboxScreenController
 * @category Hook - Controller Layer
 * @since 4.0.0
 */

import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setSelectedConversation } from '../../../../actions/inbox/conversationActions';
import useInboxData from '../data/useInboxData';
import useInboxFilters from '../data/useInboxFilters';
import useInboxUI from '../ui/useInboxUI';
import useFiltersScreenUI from '../ui/useFiltersScreenUI';
import useInboxBusinessUI from '../ui/useInboxBusinessUI';
import useInboxScreenUI from '../ui/useInboxScreenUI';
import useInboxScreenMobileUI from '../ui/useInboxScreenMobileUI';
import useInboxSocketEvents from '../data/useInboxSocketEvents';
import useInboxSocketActions from '../actions/useInboxSocketActions';
import useInboxSocketConfig from '../ui/useInboxSocketConfig';
import { useTraceUpdate } from '../../shared/useDebugRenders';
import useInboxOnboardingController from './useInboxOnboardingController';
import useInboxNotifications from '../ui/useInboxNotifications';

const useInboxScreenController = ({ sidebar = false } = {}) => {
  const dispatch = useDispatch();
  const selectedConversationId = useSelector((state) => state.conversationsReducer.selectedConversationId);

  const clearSelectedConversation = useCallback(() => {
    dispatch(setSelectedConversation(null));
  }, [dispatch]);
  const {
    showMobileRightSidebar,
    isDesktopLayout,
    handleToggleMobileRightSidebar,
    handleCloseMobileRightSidebar,
    handleMobileCloseConversation
  } = useInboxScreenMobileUI({
    selectedConversationId,
    clearSelectedConversation
  });

  /**
   * ---------------------------
   * DATA INITIALIZATION
   * ---------------------------
   */
  useInboxData();

  /**
   * ---------------------------
   * FILTERS (DATA LAYER)
   * ---------------------------
   */
  const {
    filter,
    sortConfig,
    loading,
    updateFilter,
    updateSortConfig,
    loadConversations,
    hasCustomFilters,
    resetFilters
  } = useInboxFilters();

  /**
   * ---------------------------
   * UI STATE
   * ---------------------------
   */
  const {
    showFiltersScreen,
    toggleFiltersScreen
  } = useFiltersScreenUI(false);

  const {
    isSelectingConversations,
    toggleSelectingConversations,
    toggleConversationSelection,
    exitSelectingConversations,
    syncConversationSelection,
    hasConversationSelected,
    selectedConversations
  } = useInboxUI();

  useInboxScreenUI();

  /**
   * ---------------------------
   * BUSINESS LOGIC
   * ---------------------------
   */
  const {
    handleFilterChange,
    handleSortChange
  } = useInboxBusinessUI({
    filter,
    updateFilter,
    updateSortConfig,
    loadConversations,
    sidebar
  });

  /**
   * ---------------------------
   * SOCKET CONFIGURATION
   * ---------------------------
   */
  const { socketEventHandlers } = useInboxSocketActions();
  const { socketConfig } = useInboxSocketConfig(socketEventHandlers);

  useInboxSocketEvents(socketConfig);

  const {
    isMiaOnboardingVisible,
    handleCloseMiaOnboarding
  } = useInboxOnboardingController();

  useInboxNotifications(); // Hook para manejar notificaciones y sonidos de nuevas conversaciones/mensajes

  const miaOnboardingProps = useMemo(() => ({
    visible: isMiaOnboardingVisible,
    onClose: handleCloseMiaOnboarding
  }), [isMiaOnboardingVisible, handleCloseMiaOnboarding]);

  /**
   * ---------------------------
   * DEBUG (DEV ONLY)
   * ---------------------------
   */
  useTraceUpdate(
    'InboxScreenController',
    {
      filter,
      sortConfig,
      loading,
      showFiltersScreen,
      isSelectingConversations,
      hasCustomFilters,
      selectedConversationId,
      showMobileRightSidebar,
      isDesktopLayout
    },
    {
      logRenderCount: true,
      logPropChanges: true
    }
  );

  /**
   * ---------------------------
   * MEMOIZED PROPS
   * ---------------------------
   */

  const sidebarProps = useMemo(
    () => ({
      filter,
      onFilterChange: handleFilterChange
    }),
    [filter, handleFilterChange]
  );

  const chatListScreenProps = useMemo(
    () => ({
      filter,
      onFilterChange: handleFilterChange,
      sortConfig,
      onSortChange: handleSortChange,
      showFiltersScreen,
      onToggleFiltersScreen: toggleFiltersScreen,
      loading,
      selectedConversations,

      isSelectingConversations,
      toggleSelectingConversations,
      toggleConversationSelection,
      exitSelectingConversations,
      syncConversationSelection,
      hasConversationSelected,
      hasCustomFilters,
      resetFilters,

      loadConversations
    }),
    [
      filter,
      handleFilterChange,
      sortConfig,
      handleSortChange,
      showFiltersScreen,
      toggleFiltersScreen,
      loading,
      selectedConversations,
      isSelectingConversations,
      toggleSelectingConversations,
      toggleConversationSelection,
      exitSelectingConversations,
      syncConversationSelection,
      hasConversationSelected,
      hasCustomFilters,
      resetFilters,
      loadConversations
    ]
  );

  const chatWindowProps = useMemo(
    () => ({
      platform: filter.channel
    }),
    [filter.channel]
  );

  /**
   * ---------------------------
   * RETURN CONTROLLER API
   * ---------------------------
   */
  return {
    sidebarProps,
    chatListScreenProps,
    chatWindowProps,
    miaOnboardingProps,
    selectedConversationId,
    clearSelectedConversation,
    showMobileRightSidebar,
    isDesktopLayout,
    handleToggleMobileRightSidebar,
    handleCloseMobileRightSidebar,
    handleMobileCloseConversation
  };
};

export default useInboxScreenController;
